// SponsorModal.tsx
import React, { useState } from "react";
import { addDoc, collection, db } from "../firebaseConfig";
import { message } from "antd";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./sponsorModal.css"; // new dedicated CSS
import CustomSelect from "./CustomSelect";
import CustomCountrySelect from "./CustomCountrySelect";

type SponsorInquiry = {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  mobileNumber: string;
  country: string;
  industry?: string;
  message?: string;
};

interface SponsorModalProps {
  isOpen: boolean;
  onClose: () => void;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }) => void;
}

const INDUSTRIES = [
  "Finance",
  "Technology",
  "Education",
  "Healthcare",
  "Hospitality",
  "Government",
  "Other",
] as const;

const SponsorModal: React.FC<SponsorModalProps> = ({
  isOpen,
  onClose,
  setSnackbar,
}) => {
  const [inquiry, setInquiry] = useState<SponsorInquiry>({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    mobileNumber: "",
    country: "",
    industry: "",
    message: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof SponsorInquiry, boolean>>
  >({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e: typeof errors = {};
    if (!inquiry.firstName) e.firstName = true;
    if (!inquiry.lastName) e.lastName = true;
    if (!inquiry.company) e.company = true;
    if (!inquiry.email) e.email = true;
    if (!inquiry.mobileNumber) e.mobileNumber = true;
    if (!inquiry.country) e.country = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
  setLoading(true);
  if (!validate()) {
    message.error("Please complete all required fields.");
    setLoading(false);
    return;
  }

  try {

    // 🔥 Send Email Notification
    const isDev = window.location.hostname.includes("localhost") || window.location.hostname.includes("staging");
    const functionUrl = "https://us-central1-icaconference-438ba.cloudfunctions.net/sendEmailV2";

    await fetch(functionUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        personalizations: [
          {
            to: [{ email: "eman@uaefma.com" }], // recipient
            dynamic_template_data: {
              firstName: inquiry.firstName,
              lastName: inquiry.lastName,
              company: inquiry.company,
              email: inquiry.email,
              mobileNumber: inquiry.mobileNumber,
              country: inquiry.country,
              industry: inquiry.industry || "N/A",
              message: inquiry.message || "N/A",
              submittedAt: new Date().toLocaleString(),
            },
          },
        ],
        from: {
          email: "registration@comms-dev.com",
          name: "ICA Conference",
        },
        subject: "New Sponsorship Inquiry Received",
        template_id: "d-915c8f69dc2446bb9d110429d959d1de", // reuse your existing SendGrid template
      }),
    });

    // ✅ Success feedback
    setSnackbar({
      open: true,
      message: "Inquiry submitted successfully!",
      severity: "success",
    });

    // reset form
    setInquiry({
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      mobileNumber: "",
      country: "",
      industry: "",
      message: "",
    });
    setErrors({});
    onClose();
  } catch (err: any) {
    setSnackbar({
      open: true,
      message: err?.message || "Error submitting inquiry.",
      severity: "error",
    });
  } finally {
    setLoading(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {/* HEADER */}
        <div className="modal-header">
          <h1 className="title">Become a Sponsor</h1>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* BODY */}
        <div className="sponsor-grid">
          <div className="grid-item">
            <input
              type="text"
              placeholder="First Name *"
              value={inquiry.firstName}
              onChange={(e) =>
                setInquiry({ ...inquiry, firstName: e.target.value })
              }
              className={errors.firstName ? "input-error" : ""}
            />
          </div>
          <div className="grid-item">
            <input
              type="text"
              placeholder="Last Name *"
              value={inquiry.lastName}
              onChange={(e) =>
                setInquiry({ ...inquiry, lastName: e.target.value })
              }
              className={errors.lastName ? "input-error" : ""}
            />
          </div>
          <div className="grid-item">
            <input
              type="text"
              placeholder="Company Name *"
              value={inquiry.company}
              onChange={(e) =>
                setInquiry({ ...inquiry, company: e.target.value })
              }
              className={errors.company ? "input-error" : ""}
            />
          </div>
          <div className="grid-item">
            <input
              type="email"
              placeholder="Business Email Address *"
              value={inquiry.email}
              onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
              className={errors.email ? "input-error" : ""}
            />
          </div>
          <div className="grid-item">
            <PhoneInput
              placeholder="Contact Number * (e.g. +971 xx xxx xxxx)"
              value={inquiry.mobileNumber || ""}
              international
              defaultCountry="AE"
              onChange={(val) =>
                setInquiry({ ...inquiry, mobileNumber: val || "" })
              }
              className={`input ${errors.mobileNumber ? "input-error" : ""}`}
            />
          </div>
          <div className="grid-item">
            <CustomCountrySelect
              value={inquiry.country}
              onChange={(val) => setInquiry({ ...inquiry, country: val })}
              className={errors.country ? "input-error" : ""}
            />
          </div>
          <div className="grid-item">
            <CustomSelect
              label="Industry (optional)"
              options={[...INDUSTRIES]}
              value={inquiry.industry || ""}
              onChange={(val) => setInquiry({ ...inquiry, industry: val })}
            />
          </div>
          <div className="grid-item message-field">
            <textarea
              placeholder="Message (optional)"
              value={inquiry.message || ""}
              onChange={(e) =>
                setInquiry({ ...inquiry, message: e.target.value })
              }
              rows={4}
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <button
            className="btn-register"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <span
                className="spinner"
                style={{
                  display: "inline-block",
                  width: 20,
                  height: 20,
                  border: "2px solid #ccc",
                  borderTop: "2px solid #e7a73b",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : (
              "Submit Inquiry"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorModal;
